import { useId, useState, type ChangeEvent } from 'react';
import { Button, Group, Input, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useImportProject } from '@/api/projects';
import { brand } from '@/theme/theme';

interface ExportDocument {
  format: 'forestum-project-export';
  version: 1;
  project: { name?: string };
  objects?: unknown[];
  analyses?: unknown[];
  reports?: unknown[];
  skipped?: { objects?: unknown[]; analyses?: unknown[]; reports?: unknown[] };
}

function isExportDocument(value: unknown): value is ExportDocument {
  const d = value as Partial<ExportDocument> | null;
  return !!d && typeof d === 'object' && d.format === 'forestum-project-export' && d.version === 1 && !!d.project;
}

const count = (list: unknown[] | undefined) => list?.length ?? 0;

/** Staff only: create a project in the organization from another system's reference file (P14). */
export function ImportProjectModal({
  organizationId,
  opened,
  onClose,
  onImported,
}: {
  organizationId: string;
  opened: boolean;
  onClose: () => void;
  onImported: (projectId: string) => void;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const importProject = useImportProject(organizationId);
  const fileInputId = useId();
  const [doc, setDoc] = useState<ExportDocument | null>(null);
  const [badFile, setBadFile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setDoc(null);
    setBadFile(false);
    setError(null);
    onClose();
  };
  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    setDoc(null);
    setBadFile(false);
    setError(null);
    if (!file) return;
    try {
      const parsed: unknown = JSON.parse(await file.text());
      if (isExportDocument(parsed)) setDoc(parsed);
      else setBadFile(true);
    } catch {
      setBadFile(true);
    }
  };
  const handleSubmit = async () => {
    if (!doc) return;
    setError(null);
    try {
      const project = await importProject.mutateAsync(doc);
      notifications.show({ color: 'forest', message: t('staff.import.imported') });
      onImported(project.public_id);
      handleClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const skipped = doc ? count(doc.skipped?.objects) + count(doc.skipped?.analyses) + count(doc.skipped?.reports) : 0;

  return (
    <Modal opened={opened} onClose={handleClose} title={t('staff.import.title')}>
      <Stack gap="md">
        <Text fz={13} c={brand.textMuted}>
          {t('staff.import.hint')}
        </Text>
        <Input.Wrapper id={fileInputId} label={t('staff.import.file')}>
          <input id={fileInputId} type="file" accept=".json,application/json" data-testid="import-file" onChange={(e) => void handleFile(e)} />
        </Input.Wrapper>
        {badFile && (
          <Text c="red" role="alert">
            {t('staff.import.badFile')}
          </Text>
        )}
        {doc && (
          <Stack gap={4}>
            <Text fz={13} c={brand.textMuted}>
              {t('staff.import.summary')}
            </Text>
            <Text fw={600}>{doc.project.name}</Text>
            <Text>{t('staff.import.objects', { count: count(doc.objects) })}</Text>
            <Text>{t('staff.import.analyses', { count: count(doc.analyses) })}</Text>
            <Text>{t('staff.import.reports', { count: count(doc.reports) })}</Text>
            {skipped > 0 && (
              <Text fz={13} c={brand.textMuted}>
                {t('staff.import.skipped', { count: skipped })}
              </Text>
            )}
          </Stack>
        )}
        {error && (
          <Text c="red" role="alert">
            {error}
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>
            {tc('common.cancel')}
          </Button>
          <Button loading={importProject.isPending} disabled={!doc} onClick={() => void handleSubmit()}>
            {t('staff.import.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
